import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Breadcrumbs from '../../components/shared/Breadcrumbs';
import AffiliateBanner from '../../components/shared/AffiliateBanner';
import CTABox from '../../components/shared/CTABox';
import TableOfContents from '../../components/shared/TableOfContents';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';

const GenericGuidePage = ({ 
  title, 
  description, 
  breadcrumbLabel, 
  breadcrumbHref,
  tocItems = [],
  content,
  faqs = []
}) => {
  return (
    <div className="py-12">
      <div className="container-wide">
        <Breadcrumbs items={[
          { label: 'Ratgeber', href: '/ratgeber' },
          { label: breadcrumbLabel, href: breadcrumbHref }
        ]} />
        
        <AffiliateBanner />

        <div className="grid lg:grid-cols-12 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-8">
            <article className="prose-reviewlex">
              <header className="mb-12">
                <h1 className="mb-6">{title}</h1>
                <p className="text-xl text-slate-600 mb-6">{description}</p>
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span>Aktualisiert: April 2026</span>
                  <span>•</span>
                  <span>Lesezeit: 8 Min.</span>
                </div>
              </header>

              {content}

              {faqs.length > 0 && (
                <section className="mb-16">
                  <h2>Häufig gestellte Fragen</h2>
                  <Accordion type="single" collapsible className="w-full">
                    {faqs.map((faq, index) => (
                      <AccordionItem key={index} value={`faq-${index}`}>
                        <AccordionTrigger className="text-left font-heading font-semibold text-navy-900">
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-slate-600">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </section>
              )}
            </article>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="space-y-8">
              {tocItems.length > 0 && <TableOfContents items={tocItems} />}
              
              <CTABox
                variant="sidebar"
                highlights={[
                  'Ab $39 + Staatsgebühren',
                  '1 Jahr Registered Agent kostenlos',
                  'Datenschutz inklusive',
                  'US-basierter Support'
                ]}
              />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

// Placeholder Guide Pages - Each renders with appropriate content
export const EINNummerPage = () => (
  <GenericGuidePage
    title="EIN Nummer beantragen — Schritt-für-Schritt Anleitung"
    description="Die EIN (Employer Identification Number) ist die Steuernummer Ihrer LLC. Hier erfahren Sie, wie Sie als Ausländer eine EIN beantragen können."
    breadcrumbLabel="EIN Nummer beantragen"
    breadcrumbHref="/ratgeber/ein-nummer-beantragen"
    tocItems={[
      { id: 'was-ist-ein', title: 'Was ist eine EIN?', level: 2 },
      { id: 'warum-brauchen', title: 'Warum brauchen Sie eine EIN?', level: 2 },
      { id: 'beantragen', title: 'EIN beantragen als Ausländer', level: 2 },
      { id: 'faq', title: 'FAQ', level: 2 },
    ]}
    content={
      <>
        <section id="was-ist-ein" className="mb-16">
          <h2>Was ist eine EIN?</h2>
          <p>
            Die <strong>Employer Identification Number (EIN)</strong> ist eine neunstellige 
            Identifikationsnummer, die vom IRS (Internal Revenue Service) vergeben wird. Sie 
            funktioniert wie eine Steuernummer für Ihr Unternehmen und ist vergleichbar mit 
            der deutschen Steuernummer.
          </p>
          <p>
            Jede LLC benötigt eine EIN für Steuerzwecke, Bankkonten und Geschäftsbeziehungen. 
            Auch wenn Ihre LLC keine Mitarbeiter hat, ist eine EIN in der Regel erforderlich.
          </p>
        </section>

        <section id="warum-brauchen" className="mb-16">
          <h2>Warum brauchen Sie eine EIN?</h2>
          <ul>
            <li><strong>Bankkonten:</strong> Alle US-Banken verlangen eine EIN für Geschäftskonten</li>
            <li><strong>Steuererklärungen:</strong> Erforderlich für die Einreichung von Steuerdokumenten</li>
            <li><strong>Geschäftspartner:</strong> Viele Partner und Kunden verlangen Ihre EIN</li>
            <li><strong>Zahlungsdienstleister:</strong> Stripe, PayPal etc. benötigen eine EIN</li>
          </ul>
        </section>

        <section id="beantragen" className="mb-16">
          <h2>EIN beantragen als Ausländer</h2>
          <p>
            Als Nicht-US-Bürger können Sie eine EIN nicht online beantragen. Es gibt zwei Optionen:
          </p>
          
          <h3>Option 1: Per Fax (Form SS-4)</h3>
          <p>
            Füllen Sie das IRS-Formular SS-4 aus und faxen Sie es an die IRS. Die Bearbeitung 
            dauert etwa 4 Wochen. Sie benötigen keine SSN (Social Security Number).
          </p>

          <h3>Option 2: Über Northwest Registered Agent</h3>
          <p>
            Northwest bietet einen EIN-Service an, der den Prozess für Sie übernimmt. 
            Dies ist oft schneller und vermeidet mögliche Fehler beim Ausfüllen.
          </p>

          <div className="card-highlight p-6 mt-8">
            <a
              href="https://www.northwestregisteredagent.com"
              target="_blank"
              rel="nofollow sponsored noopener noreferrer"
              className="cta-primary inline-flex"
            >
              EIN mit Northwest beantragen
              <ArrowRight className="w-4 h-4 ml-2" />
            </a>
          </div>
        </section>
      </>
    }
    faqs={[
      {
        question: 'Wie lange dauert es, eine EIN zu bekommen?',
        answer: 'Per Fax dauert es etwa 4 Wochen. Über einen Service wie Northwest kann es schneller gehen, da sie Erfahrung mit dem Prozess haben und Fehler vermeiden.'
      },
      {
        question: 'Kostet die EIN etwas?',
        answer: 'Die EIN selbst ist kostenlos vom IRS. Wenn Sie einen Dienstleister wie Northwest nutzen, fällt eine Service-Gebühr an (typischerweise $50-100).'
      },
      {
        question: 'Brauche ich eine ITIN für die EIN?',
        answer: 'Nein, Sie können eine EIN ohne ITIN oder SSN beantragen. Im Formular SS-4 geben Sie im Feld für SSN/ITIN "Foreign" an.'
      },
    ]}
  />
);

export const USBankkontoPage = () => (
  <GenericGuidePage
    title="US-Bankkonto eröffnen als Ausländer — Der komplette Guide"
    description="Ein US-Geschäftskonto für Ihre LLC ist essentiell. Hier zeigen wir, welche Banken Ausländer akzeptieren und wie der Prozess funktioniert."
    breadcrumbLabel="US-Bankkonto eröffnen"
    breadcrumbHref="/ratgeber/us-bankkonto-eroeffnen-auslaender"
    tocItems={[
      { id: 'warum-us-konto', title: 'Warum ein US-Bankkonto?', level: 2 },
      { id: 'beste-banken', title: 'Beste Banken für Ausländer', level: 2 },
      { id: 'dokumente', title: 'Erforderliche Dokumente', level: 2 },
      { id: 'faq', title: 'FAQ', level: 2 },
    ]}
    content={
      <>
        <section id="warum-us-konto" className="mb-16">
          <h2>Warum ein US-Bankkonto?</h2>
          <p>
            Ein US-Geschäftskonto für Ihre LLC bietet zahlreiche Vorteile: US-Dollar-Zahlungen 
            ohne Wechselkursgebühren, Zugang zu US-Zahlungsdienstleistern wie Stripe, 
            professionelleres Erscheinungsbild gegenüber US-Kunden.
          </p>
        </section>

        <section id="beste-banken" className="mb-16">
          <h2>Beste Banken für Ausländer</h2>
          
          <div className="space-y-4">
            <div className="card-flat p-6">
              <h4 className="font-heading font-bold text-navy-900 mb-2">Mercury</h4>
              <p className="text-slate-600 text-sm mb-0">
                Unsere Top-Empfehlung. Mercury ist eine digitale Bank, die speziell auf 
                Startups und Online-Businesses ausgerichtet ist. Kontoeröffnung komplett 
                online möglich, auch für Ausländer.
              </p>
            </div>

            <div className="card-flat p-6">
              <h4 className="font-heading font-bold text-navy-900 mb-2">Relay</h4>
              <p className="text-slate-600 text-sm mb-0">
                Ähnlich wie Mercury, ebenfalls online eröffnbar. Gute Option für kleine 
                Businesses mit Fokus auf einfache Buchhaltung.
              </p>
            </div>

            <div className="card-flat p-6">
              <h4 className="font-heading font-bold text-navy-900 mb-2">Wise Business</h4>
              <p className="text-slate-600 text-sm mb-0">
                Nicht direkt ein US-Bankkonto, aber Wise bietet US-Kontonummern für 
                internationale Unternehmen. Ideal für Empfang von US-Zahlungen.
              </p>
            </div>
          </div>
        </section>

        <section id="dokumente" className="mb-16">
          <h2>Erforderliche Dokumente</h2>
          <ul>
            <li>Articles of Organization (Certificate of Formation)</li>
            <li>EIN (Employer Identification Number)</li>
            <li>Operating Agreement</li>
            <li>Reisepass des LLC-Inhabers</li>
            <li>Nachweis der Geschäftsadresse</li>
          </ul>
        </section>
      </>
    }
    faqs={[
      {
        question: 'Kann ich ein US-Konto komplett online eröffnen?',
        answer: 'Ja, Banken wie Mercury und Relay ermöglichen die vollständige Online-Kontoeröffnung für LLCs, auch wenn der Inhaber außerhalb der USA lebt.'
      },
      {
        question: 'Wird mein Antrag abgelehnt?',
        answer: 'Es ist möglich. Banken prüfen jeden Antrag individuell. Eine vollständig eingerichtete LLC mit allen Dokumenten erhöht Ihre Chancen erheblich.'
      },
    ]}
  />
);

export default GenericGuidePage;
